import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTelephony } from "../../context/TelephonyContext";
import { I } from "../redesign/kvpIcons";

/**
 * Dialpad — taktiler Nummernblock für die LINKE Spalte der TelephonyPage (SP5,
 * Mockup #9). Ersetzt den früheren `tp-dialpad-col`-Platzhalter.
 *
 *  - Anzeige-Feld mit der getippten Nummer (auch direkt per Tastatur editierbar).
 *  - 12 Tasten (1-9, *, 0(+), #) mit den klassischen Buchstaben-Untertiteln.
 *  - Backspace + „Anrufen" (gold) → `callNumber(nummer)`.
 *  - Tastatur: Ziffern/`*`/`#`/`+` tippen in die Anzeige, Enter ruft an,
 *    Backspace löscht (alles über das fokussierte Input).
 *  - „Anrufen" ist bei leerer Eingabe deaktiviert.
 *
 * KEIN „SMS"-Button (Plan #9: SMS entfällt). Aktive Anrufe übernimmt das
 * globale CallWidget; der Dialpad löst nur das Wählen aus.
 *
 * `onAfterDial` (optional, SP7): wird nach einem erfolgreichen Wählversuch
 * aufgerufen — der Mobil-Zugang (< 900px, ein-/ausklappbares Sheet) schließt sich
 * darüber selbst.
 */

const KEYS: { d: string; sub: string }[] = [
    { d: "1", sub: "" },
    { d: "2", sub: "ABC" },
    { d: "3", sub: "DEF" },
    { d: "4", sub: "GHI" },
    { d: "5", sub: "JKL" },
    { d: "6", sub: "MNO" },
    { d: "7", sub: "PQRS" },
    { d: "8", sub: "TUV" },
    { d: "9", sub: "WXYZ" },
    { d: "*", sub: "" },
    { d: "0", sub: "+" },
    { d: "#", sub: "" },
];

// Erlaubte Wähl-Zeichen (Ziffern + die für SIP/DTMF üblichen Sonderzeichen).
const ALLOWED = /[0-9*#+\s()/-]/;
const sanitize = (s: string): string =>
    Array.from(s)
        .filter((ch) => ALLOWED.test(ch))
        .join("");

const BackspaceIcon = (
    <svg
        width={20}
        height={20}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M7 4.5h9.5A1.5 1.5 0 0118 6v8a1.5 1.5 0 01-1.5 1.5H7L2.5 10z" />
        <path d="M13.5 8l-4 4M9.5 8l4 4" />
    </svg>
);

const Dialpad: React.FC<{ onAfterDial?: () => void }> = ({ onAfterDial }) => {
    const { callNumber, sdkStatus } = useTelephony();
    const [value, setValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const registered = sdkStatus === "registered";
    const statusHint =
        sdkStatus === "connecting"
            ? "Telefonie verbindet …"
            : sdkStatus === "error"
              ? "Telefonie-Verbindung fehlgeschlagen"
              : "Telefonie nicht verbunden";

    const append = useCallback((ch: string) => {
        setValue((prev) => prev + ch);
        // Fokus zurück aufs Eingabefeld, damit weiteres Tippen direkt landet.
        inputRef.current?.focus();
    }, []);

    const backspace = useCallback(() => {
        setValue((prev) => prev.slice(0, -1));
        inputRef.current?.focus();
    }, []);

    const dial = useCallback(() => {
        if (!registered) return;
        const n = value.trim();
        if (!n) return;
        callNumber(n);
        onAfterDial?.();
    }, [value, callNumber, registered, onAfterDial]);

    // Enter im Eingabefeld → anrufen.
    const onInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                dial();
            }
        },
        [dial],
    );

    // Beim Mount Fokus aufs Feld, damit man sofort lostippen kann.
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const canDial = registered && value.trim().length > 0;

    return (
        <div className="tp-dialpad" aria-label="Tastenfeld">
            <div className="tp-dp-display">
                <input
                    ref={inputRef}
                    className="tp-dp-input"
                    value={value}
                    onChange={(e) => setValue(sanitize(e.target.value))}
                    onKeyDown={onInputKeyDown}
                    placeholder="Nummer eingeben"
                    inputMode="tel"
                    aria-label="Telefonnummer"
                    autoComplete="off"
                />
                <button
                    type="button"
                    className="tp-dp-back"
                    onClick={backspace}
                    disabled={!value}
                    aria-label="Letzte Ziffer löschen"
                    title="Löschen"
                >
                    {BackspaceIcon}
                </button>
            </div>

            <div className="tp-dp-grid" role="group" aria-label="Ziffernblock">
                {KEYS.map((k) => (
                    <button
                        key={k.d}
                        type="button"
                        className="tp-dp-key"
                        onClick={() => append(k.d)}
                        aria-label={k.d}
                    >
                        <span className="tp-dp-key-d">{k.d}</span>
                        {k.sub && <span className="tp-dp-key-sub">{k.sub}</span>}
                    </button>
                ))}
            </div>

            <button
                type="button"
                className="tp-dp-call"
                onClick={dial}
                disabled={!canDial}
                aria-label="Anrufen"
                title={registered ? "Anrufen" : statusHint}
            >
                {I.phone}
                Anrufen
            </button>

            {!registered && <div className="tp-dp-hint">{statusHint}</div>}
        </div>
    );
};

export default Dialpad;
